import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormGetValues, UseFormUnregister } from 'react-hook-form';

import type { Position, Alignment } from 'af-components/Controls/ColorPicker/ColorsGrid';
import { ColorPicker } from 'af-components/RHFFieldWrappers/ColorPicker/ColorPicker';
import { ColorPickerField } from 'af-components/RHFTableCellComponents/ColorPicker/DisplayCell';

import EditModeCellWrapper from './EditModeCellWrapper';

type Props<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
	TTransformedValues
> = UseControllerProps<TFieldValues, TName, TTransformedValues> & {
	rhfId: string | number;
	unregister: UseFormUnregister<TFieldValues>;
	getValues: UseFormGetValues<TFieldValues>;
	fieldsLength: number;
	readonly?: boolean;

	position: Position;
	align: Alignment;
	showAdditionalOptions?: boolean;
	disabled?: boolean;
	fixed?: boolean;

	onChange?: (name: string) => void;
};

export function ColorPickerCellRenderer<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, readonly, onChange } = props;

	const handleOnChange = React.useCallback((fieldName: string) => {
		onChange?.(fieldName);
	}, [onChange]);

	return (
		<EditModeCellWrapper name={name} readonly={readonly}>
			{(shouldBeRegistered, exitEditMode) =>
				shouldBeRegistered ? (
					<ColorPickerField<TFieldValues, TName, TTransformedValues>
						{...props}
						onBlur={exitEditMode}
						onChange={handleOnChange}
					/>
				) : (
					<ColorPicker<TFieldValues, TName, TTransformedValues>
						{...props}
					/>
				)
			}
		</EditModeCellWrapper>
	);
}

export default React.memo(ColorPickerCellRenderer) as typeof ColorPickerCellRenderer;
